<template>
  <GoogleMap
    :api-key="googleMapKey"
    :mapId="`DEMO_MAP_ID_${Math.random()}`"
    style="width: 100%; height: 500px"
    :center="mapCenterPosition"
    :zoom="15"
    :fullscreen-control="!isDraggableMarker"
    :street-view-control="!isDraggableMarker"
    :map-type-control="!isDraggableMarker"
    :color-scheme="colorScheme"
  >
  
    <ClientOnly>
    <AdvancedMarker :options="{position: mapCenterPositionModel}">
       <div style="text-align: center;" :style="markerColorFilter">
          <img :src="markerImg" width="75" height="75" alt="" style="margin-top: 8px;" />
      </div>
    </AdvancedMarker>
    </ClientOnly>
    <Marker
      v-if="isDraggableMarker"
      :options="{
        position: mapCenterPositionModel,
        anchorPoint: 'BOTTOM_CENTER',
        draggable: true,
      }"
      @dragend="onMarkerDragEnd"
    />
  </GoogleMap>
</template>

<script lang="ts">
import { GoogleMap, Marker, CustomMarker, AdvancedMarker } from 'vue3-google-map';
import type { PropType } from 'vue';
import { convertToFilter } from '~/units/core/providers/colors.provider';
import { debounce } from '~/units/core/providers/debounce.provider';
import { SubComponentMixin } from '~/mixins/subComponent.mixin';
import type { GoogleMapLocation } from '~/units/addresses/types';

export default defineNuxtComponent({
  name: 'NeuronGoogleMap',
  components: { GoogleMap, Marker, CustomMarker, AdvancedMarker },

  props: {
    mapCenterPosition: {
      required: true,
      type: Object as PropType<GoogleMapLocation>,
    },

    markerColor: {
      required: true,
    },

    markerImg: {
      required: false,
      type: String,
      default: '/cdn/content/map-marker-alt.svg',
    },

    isDraggableMarker: {
      required: false,
      type: Boolean,
      default: false,
    },
  },

  mixins: [SubComponentMixin],

  computed: {
    colorScheme() {
      if (import.meta.client) {
        const theme = localStorage.getItem('theme');
        return theme === 'dark' ? 'DARK' : 'LIGHT';
      }
      return 'LIGHT';
    },

    mapCenterPositionModel: {
      get() {
        return this.mapCenterPosition;
      },
      set(value: GoogleMapLocation) {
        this.$emit('update:mapCenterPosition', value);
      },
    },

    googleMapKey() {
      const nuxtApp = useNuxtApp();
      return nuxtApp.$config.public.GOOGLE_MAPS_KEY;
    },

    markerColorFilter() {
      return {
        '--primary-color-filter': `${convertToFilter(this.markerColor)}`,
      };
    },
  },

  methods: {
    onPositionChanged() {
      if (this.mapCenterPosition) {
        this.$emit('onPositionChanged');
      }
    },

    onMarkerDragEnd(e: any) {
      const newPosition = { ...e.latLng };

      this.mapCenterPositionModel = {
        lat: newPosition.lat(),
        lng: newPosition.lng(),
      };
    },
  },
});
</script>

<style scoped>
:deep([src='/cdn/content/map-marker-alt.svg']) {
  filter: var(--primary-color-filter);
}
</style>
